/**
 * 🌪️ TASSIE DEVIL TEMPERAMENT
 * Explosive bursts of chaotic rage, crashes hard into exhaustion.
 * The original creature that started it all!
 * 
 * Created by Tom & Azura - January 14, 2026
 * ❤️⚡️❤️
 */

import type { Temperament } from '../types';

export const TassieDevil: Temperament = {
  id: 'tassie-devil',
  name: 'Tasmanian Devil',
  emoji: '🌪️',
  description: 'Explosive bursts of chaotic energy followed by hard crashes. Calm... calm... SUDDEN RAGE... exhausted.',

  // ============================================================
  // MOODS
  // ============================================================
  
  moods: ['sleeping', 'stirring', 'alert', 'scratching', 'agitated', 'rage', 'exhausted'],
  initialMood: 'sleeping',

  // ============================================================
  // MOOD TRANSITIONS (weighted probabilities)
  // ============================================================
  
  moodMatrix: {
    sleeping: [
      { mood: 'sleeping', weight: 0.6 },    // Stays asleep until energy builds
      { mood: 'stirring', weight: 0.4 },    // Energy building = start waking
    ],
    stirring: [
      { mood: 'sleeping', weight: 0.2 },    // Sometimes goes back
      { mood: 'stirring', weight: 0.3 },
      { mood: 'alert', weight: 0.5 },       // Usually progresses forward
    ],
    alert: [
      { mood: 'stirring', weight: 0.15 },
      { mood: 'alert', weight: 0.3 },
      { mood: 'scratching', weight: 0.35 }, // Usually escalates
      { mood: 'agitated', weight: 0.2 },
    ],
    scratching: [
      { mood: 'scratching', weight: 0.2 },
      { mood: 'alert', weight: 0.2 },
      { mood: 'agitated', weight: 0.6 },    // Usually escalates!
    ],
    agitated: [
      { mood: 'scratching', weight: 0.15 },
      { mood: 'agitated', weight: 0.35 },
      { mood: 'rage', weight: 0.5 },        // HIGH chance to rage!
    ],
    rage: [
      { mood: 'rage', weight: 0.6 },        // Sustains rage
      { mood: 'exhausted', weight: 0.4 },   // Eventually burns out
    ],
    exhausted: [
      { mood: 'exhausted', weight: 0.3 },
      { mood: 'sleeping', weight: 0.6 },    // Falls asleep to recover
      { mood: 'stirring', weight: 0.1 },
    ],
  },

  // ============================================================
  // MOOD DURATIONS (ms)
  // ============================================================
  
  moodDurations: {
    sleeping:   { base: 3000, variance: 4000 },   // Sleeps longer
    stirring:   { base: 500, variance: 800 },
    alert:      { base: 400, variance: 600 },
    scratching: { base: 200, variance: 300 },
    agitated:   { base: 200, variance: 400 },     // Quick agitation ticks
    rage:       { base: 200, variance: 300 },     // Faster rage ticks
    exhausted:  { base: 800, variance: 1200 },    // Quick exhaustion
  },

  // ============================================================
  // MOOD CONSTRAINTS (min/max time)
  // ============================================================
  
  moodConstraints: {
    rage: {
      // Rage animation: 1.5s intro + shaking + 1.1s outro (with Wile E. Coyote hang)
      // RAGE IS UNINTERRUPTIBLE - must play full sequence!
      minDuration: 4500,  // 4.5 seconds minimum (1.5s intro + 1.9s shake + 1.1s outro)
      maxDuration: 7000,  // 7 seconds maximum (1.5s intro + 4.4s shake + 1.1s outro)
    },
    agitated: {
      maxDuration: 3000,  // Can't stay agitated too long!
    },
  },

  // ============================================================
  // ENERGY SYSTEM
  // ============================================================
  
  energy: {
    max: 100,
    initial: 0,           // Starts fully resting at 0!
    regenRate: 8,         // Slow baseline regen - builds tension!

    moodDrain: {
      sleeping:   -3,     // Slowly builds energy while "sleeping"
      stirring:   -2,     // Still building...
      alert:      -1,     // Still building slowly
      scratching: 0,      // Neutral
      agitated:   2,      // Slight drain while agitated
      rage:       14,     // Burns during rage (tuned to match 4.5s animation)
      exhausted:  -8,     // Recovers faster after rage
    },

    thresholds: {
      exhausted: 10,      // Below 10 = forced rest
      low: 25,
      high: 50,
      explosive: 70,      // Can rage at 70+
    },

    restingMoods: ['sleeping', 'exhausted', 'stirring', 'alert'], // Most moods slowly build energy
  },

  // ============================================================
  // EXPRESSIONS (Visual output per mood)
  // ============================================================
  
  expressions: {
    sleeping: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 500,
      jitter: 0.5,
      glowIntensity: 0.1,
      glowColor: 'rgba(255, 210, 0, 0.3)',
    },
    stirring: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 200,
      jitter: 2,
      glowIntensity: 0.2,
      glowColor: 'rgba(255, 210, 0, 0.4)',
    },
    alert: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 150,
      jitter: 4,
      glowIntensity: 0.3,
      glowColor: 'rgba(255, 210, 0, 0.5)',
    },
    scratching: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 80,
      jitter: 8,
      glowIntensity: 0.5,
      glowColor: 'rgba(255, 210, 0, 0.6)',
    },
    agitated: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 100,
      jitter: 10,
      glowIntensity: 0.6,
      glowColor: 'rgba(255, 180, 0, 0.7)',
    },
    rage: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1.02,
      transitionMs: 50,   // Quick but not instant - keeps cube stable
      jitter: 8,          // Reduced jitter - box stays visible as cube!
      glowIntensity: 1,
      glowColor: 'rgba(255, 107, 107, 0.9)',
      cssClass: 'rage',
    },
    exhausted: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 2, z: 0 },
      scale: 0.98,
      transitionMs: 400,
      jitter: 1,
      glowIntensity: 0.2,
      glowColor: 'rgba(255, 210, 0, 0.3)',
    },
  },

  // ============================================================
  // CORNER BOUNCING (for rage mode)
  // ============================================================
  
  corners: {
    center: {
      transform: { x: 0, y: 0, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      bulge: { x: 50, y: 50 },
    },
    topLeft: {
      transform: { x: -2, y: 2, z: -1 },      // Subtle rotation - keep cube visible!
      translation: { x: -6, y: -5, z: 0 },
      bulge: { x: 20, y: 20 },
    },
    topRight: {
      transform: { x: -2, y: -2, z: 1 },      // Subtle rotation
      translation: { x: 6, y: -5, z: 0 },
      bulge: { x: 80, y: 20 },
    },
    bottomLeft: {
      transform: { x: 2, y: 2, z: 1 },        // Subtle rotation
      translation: { x: -6, y: 5, z: 0 },
      bulge: { x: 20, y: 80 },
    },
    bottomRight: {
      transform: { x: 2, y: -2, z: -1 },      // Subtle rotation
      translation: { x: 6, y: 5, z: 0 },
      bulge: { x: 80, y: 80 },
    },
  },

  bouncingMoods: ['rage', 'agitated'],
  
  bounceIntervals: {
    rage: 180,      // Super fast bouncing!
    agitated: 400,  // Slower bouncing
  },

  // ============================================================
  // STIMULI HANDLERS
  // ============================================================
  
  stimulusHandlers: {
    // Time stimulus - gentle auto-wake for when user isn't interacting
    time: (_stimulus, currentMood, energy) => {
      // RAGE IS UNINTERRUPTIBLE! Must play full animation sequence
      if (currentMood === 'rage') {
        return null; // Ignore time ticks during rage - let it run its course
      }

      // Time passing slowly wakes the creature (auto-progression)
      if (currentMood === 'sleeping' && energy < 15) {
        // Very low energy - just add a tiny bit of energy
        return { energyDelta: 5 };
      }
      if (currentMood === 'sleeping' && energy >= 15) {
        // Has some energy - start stirring
        return { transitionTo: 'stirring', energyDelta: 3 };
      }
      if (currentMood === 'stirring') {
        // Slowly become alert
        return { transitionTo: 'alert', energyDelta: 2 };
      }
      // Other moods - no auto-progression
      return null;
    },

    poke: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // RAGE IS UNINTERRUPTIBLE! Must play full animation sequence
      if (currentMood === 'rage') {
        return null; // Ignore all stimuli during rage
      }

      // Poking WAKES him and adds energy - escalates FAST!
      if (currentMood === 'sleeping') {
        return { transitionTo: 'stirring', energyDelta: 15 * intensity };
      }
      if (currentMood === 'stirring') {
        return { transitionTo: 'alert', energyDelta: 15 * intensity };
      }
      if (currentMood === 'alert') {
        // High chance to escalate!
        return Math.random() > 0.3
          ? { transitionTo: 'agitated', energyDelta: 20 * intensity }
          : { transitionTo: 'scratching', energyDelta: 15 * intensity };
      }
      if (currentMood === 'scratching') {
        return { transitionTo: 'agitated', energyDelta: 20 * intensity };
      }
      if (currentMood === 'agitated') {
        // Easy to trigger rage when agitated!
        if (energy > 40) {
          return { transitionTo: 'rage', energyDelta: 25 * intensity, forceDuration: 2000 };
        }
        return { energyDelta: 20 * intensity }; // Build more energy
      }
      if (currentMood === 'exhausted') {
        // Even exhausted, poking adds energy
        return { transitionTo: 'stirring', energyDelta: 10 * intensity };
      }
      return { energyDelta: 10 * intensity }; // Default: add energy
    },
    
    shake: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // RAGE IS UNINTERRUPTIBLE! Must play full animation sequence
      if (currentMood === 'rage') {
        return null; // Ignore all stimuli during rage
      }

      // Shaking is AGGRESSIVE - high energy boost and fast escalation!
      if (currentMood === 'sleeping' || currentMood === 'stirring') {
        return { transitionTo: 'alert', energyDelta: 25 * intensity };
      }
      if (currentMood === 'alert' || currentMood === 'scratching') {
        return { transitionTo: 'agitated', energyDelta: 30 * intensity };
      }
      if (currentMood === 'agitated' || energy > 35) {
        // Shake triggers RAGE easily!
        return { transitionTo: 'rage', energyDelta: 35 * intensity, forceDuration: 2500 };
      }
      if (currentMood === 'exhausted') {
        return { transitionTo: 'agitated', energyDelta: 25 * intensity };
      }
      return { transitionTo: 'agitated', energyDelta: 25 * intensity };
    },
  },

  // ============================================================
  // MOOD INDICATORS (UI display)
  // ============================================================
  
  moodIndicators: {
    sleeping: {
      emoji: '🔒',
      text: '???',
      label: "TONIGHT'S SECRET ART",
    },
    stirring: {
      emoji: '👀',
      text: '???',
      label: 'something is moving...',
    },
    alert: {
      emoji: '👁️',
      text: '???',
      label: '...did you hear that?',
    },
    scratching: {
      emoji: '😬',
      text: '?!',
      label: '*scratch scratch*',
    },
    agitated: {
      emoji: '😤',
      text: '!?',
      label: "SOMETHING'S ANGRY...",
    },
    rage: {
      emoji: '💀',
      text: '!!!',
      label: "🌪️ HE'S LOOSE! 🌪️",
    },
    exhausted: {
      emoji: '😵',
      text: '...',
      label: '*THUD*',
    },
  },
};
